class CartNotification extends HTMLElement {

  // The section to add products into
  productsList;

  // The id of the section / element that contains the cart item count
  cartItemCountId = 'cart-icon-bubble';

  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    // The location where products are to be added and reset on close
    this.productsList = document.getElementById('cart-notification__products');

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Buttons must have a data-close-notification-button attribute to be a close button otherwise their activity is handled elsewhere
    this.querySelectorAll('button[data-close-notification-button]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    this.addEventListener('change',debounce(
      this.onCartUpdate.bind(this)
    ));
  }

  /**
   * Update the quantity of a single item based on the cart item key (rather than it's position in the cart)
   * @param {string} cartItemKey The cart item key to update
   * @param {number} quantity The quantity to set for the item
   */
  async updateCartItemQuantity(cartItemKey, quantity) {

    const body = JSON.stringify({      
      id: cartItemKey,
      quantity: quantity
    });

    return fetch(routes.cart_change_url,  { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        return parsedState;
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  }

  /**
   * Handle the cart being update (quantity / item removed)
   * @param {Event} event The change event (triggered by the quantity input change)
   */
  async onCartUpdate(event) {
      const target = event.target;
      // If this is not a quantity input we can ignore it (at least for the moment)
      if (!target.classList.contains('quantity__input')) return;

      // Find the closest quatity input container to get the cart item key
      const quantityInput = target.closest('quantity-input');

      try {
        // Attempt to put the quantity input into the loading state
        quantityInput.startLoading();
      } catch (error) {
        console.error('Error starting quantity input loading state:', error);
      }

      // Find the current quantity on the input
      const quantity = parseInt(target.value,10);

      // Get the correct product to update (we might have multiple products in the notification)
      const productElement = target.closest('.cart-notification-product');

      if (!productElement || !productElement.dataset.cartItemKey) {
        console.error('Could not find cart item key for the product to update.');
        return;
      }
      this.updateCartItemQuantity(productElement.dataset.cartItemKey, quantity)
        .then(() => { 
          quantityInput.stopLoading();
          // Update the count of the items in the cart bubble
          this.updateCartBubble();
          if (quantity === 0) {
            // If there is only one product then we can close the notification after removing the product
            const variantId = target?.dataset?.quantityVariantId;
            this.removeProductFromNotification(variantId)
                  .then(() => {
                    // Remove the updating state from the quantity input now
                    target.classList.remove('updating');
                  });
          } else {
            // We can remove the updating state from the quantity input now
            target.classList.remove('updating');
          }
         })
        .catch((error) => { 
          console.error('Error:', error);
          alert('There was an error updating the cart item quantity. Please try again.');
        });
  }

  /**
   * Re-render the cart bubble to reflect the current cart state
   */
  async updateCartBubble() {
    // Get the current version of the section from the server
    return fetch(`/?section_id=${this.cartItemCountId}`)
      .then((response) => {   
        return response.text();
      })
      .then((innerHTML) => {   
        // So long as we got something back then update the cart bubble section with the new data
        if (innerHTML)     
          document.getElementById(this.cartItemCountId).innerHTML = innerHTML;
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  }

  /**
   * Remove the product from the notification and close the notification slowly by reducing the opacity and then close the notification
   * @param {string} variantId The variant that is being removed from the cart
   */
  async removeProductFromNotification(variantId) {
    return new Promise((resolve) => {   
      const shownProducts = this.productsList.querySelectorAll('.cart-notification-product');
      if (shownProducts.length === 0) {
        resolve();
        return;
      }
      let found = false;
      for (const product of shownProducts) {
        if (variantId && product.id.startsWith(`cart-notification-product-${variantId}`)) {
          found = true;
          product.classList.add('deleted');
          setTimeout(() => {
            // If this is the last prodyct close the notification
            product.remove();
            // Check how many are left, if none then close the notification
            if (!this.productsList.querySelectorAll('.cart-notification-product').length)
              this.close();

            // Reset the product element state for next time
            product.classList.remove('deleted');
            resolve();
          }, 500);
          break;
        }
      }
      if (!found) resolve();
    });
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);

    // Reset the list of products to just be one empty product
    if(this?.productsList) {
      this.productsList.innerHTML = '';
    }

  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      
      // If the product has an existing element then append the new one to it
      if (section?.append && this.productsList) {
        const newElement = new DOMParser().parseFromString(parsedState.sections[section.id], 'text/html')
                                .querySelector(section.selector);

        // If the element already exists i.e. we are adding a second product then append the new one
        const existingElement = this.productsList.querySelector(`[id="${newElement.id}"]`);
        if (existingElement) {
          existingElement.replaceWith(newElement);
        } else {
          this.productsList.append(newElement);
        }
      } else {
        document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );  
      }
    });


    if (this.header) this.header.reveal();
    this.open();
  }

  /**
   * Return the list of sections to be updated
   * @returns The sections that will be re-rendered from a cart add response
   */
  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
        append: true
      },
      {
        id: this.cartItemCountId,
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
